import { FC, useEffect, useState, useMemo, useRef } from "react";
import { Box, useTheme, IconButton, Tooltip } from "@mui/material";
import { FormikHelpers } from "formik";
import { ReactTable, Table, toast } from "@vilocnv/allsetra-core";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getXarReportErrorMessage } from "app/data/utils/errorUtils";

import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useCurrentUser
} from "hooks";
import {
  commonReportsDataFormatter,
  commonReportsFilterFormValidationSchema,
  convertDatesForFilterPayload,
  convertToNormalTime,
  getStandardizedReportDates,
  reportTransformTimeForAPI,
  xarReportFormInitialValues,
  xarReportFormValidationSchema,
  xarReportFilterInitialValues,
  xarReportFilterValidationSchema
} from "app/data/helpers";
import { getResetFilterFormValues } from "app/data/helpers/ReportHelpers";
import { downloadReportFile } from "app/data/constants/Reports/exportedReportHistoryConstants";
import {
  generateReportThunk,
  generateXarReportThunk,
  listXarReportThunk
} from "app/features/reports/reportsActions";
import {
  getAllKeysByAccountThunk,
  setReportsFilterData,
  setReportsDateValues,
  getUsersBasicInfoThunk,
  resetReportsFilterData
} from "app/features";
import { useTranslation } from "react-i18next";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import {
  selectAccountKeysState,
  selectAccountsState,
  selectReportsState
} from "app/data/selectors";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";
import { XAR_REPORT_TABLE_COLUMNS } from "app/data/constants/Reports/xarReportsConstants";
import { ReportsService, ObjectsService } from "app/data/services";
import GenerateXarReportForm from "components/forms/Reports/GenerateXarReportForm";
import { ADMIN_ROLES } from "app/data/helpers/rolePermissions";
import { useNavigate } from "react-router-dom";

interface IApiPayload {
  accountId?: string;
  forAccountId?: string;
  driverId?: string;
  objectIds?: string[];
  startDate?: string;
  endDate?: string;
  reportType?: number;
  assignedToAllObjects?: boolean;
  [key: string]: any;
}

const XarReports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentRoleName } = useCurrentUser();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleOnChangeReportFilterForm,
    handleAccountSearch,
    searchedMinimalAccounts,
    searchedMinimalAccountsLoading,
    handleSkrrsObjectsSearch,
    skrrsObjects,
    skrrsLoading,
    reportsFilterData,
    filterValues,
    setFilterValues,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const { usersBasicInfo } = useAppSelector(selectAccountsState);
  const { xarReport, xarReportLoading } = useAppSelector(selectReportsState);

  const [openModal, setOpenModal] = useState(false);
  const [openGenerateForm, setOpenGenerateForm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [listingDateRange, setListingDateRange] = useState<any>({
    startDate: null,
    endDate: null
  });
  const [generateDateRange, setGenerateDateRange] = useState<any>({
    startDate: null,
    endDate: null
  });

  const lastRequestRef = useRef<string>("");
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const handleDownload = async (row: any) => {
    toast.info("Starting download...");
    try {
      const payload: any = {
        uniqueId: row?.uniqueId,
        forAccountId: row?.forAccountId,
        accountId: row?.accountId,
        startDate: row?.reportingStartDate,
        endDate: row?.reportingEndDate,
        reportType: 8
      };

      if (row?.driverId) {
        payload.driverId = row.driverId;
      }

      const response = await ReportsService.downloadXarReport(payload);

      const filename = `XAR_Report_${row?.uniqueId || "report"}_${new Date()?.toISOString()?.slice(0, 10)}.xar`;

      downloadReportFile(response, filename);

      toast.success("Downloaded successfully");
    } catch (error: any) {
      console.error("Download failed:", error);
      const errorMessage = getXarReportErrorMessage(error, "download");
      toast.error(errorMessage);
    }
  };

  const { formattedData, isShowKeyCol } = commonReportsDataFormatter(
    localReport,
    "name",
    dateFormat
  );
  const tableColumns = useMemo(
    () => XAR_REPORT_TABLE_COLUMNS(t, handleDownload),
    [t, isShowKeyCol]
  );

  const [apiPayload, setApiPayload] = useState<IApiPayload | null>(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? (reportsFilterData as IApiPayload)
      : null
  );

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const co2ReportFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    setFilterValues(values);

    const startDate = convertToNormalTime(values.dateRange.startDate);
    const endDate = convertToNormalTime(values.dateRange.endDate, true);

    const payload = {
      startDate,
      endDate,
      objectIds: values.assignedToAllObjects ? [] : values.objectIds,
      reportType: 8,
      driverId: values.driverId || undefined
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    const xarPayload: any = {
      accountId: drawerSelectedAccountId,
      reportType: 8,
      startDate,
      endDate,
      objectIds: values.assignedToAllObjects ? [] : values.objectIds || []
    };

    if (values.driverId) {
      xarPayload.driverId = values.driverId;
    }

    try {
      const { type, payload: responsePayload } = await dispatch(
        listXarReportThunk(xarPayload)
      );

      if (type === "reports/listXarReportThunk/fulfilled") {
        formikHelpers.setSubmitting(false);
        setFilterOpen(false);
      } else {
        const errorMessage = getXarReportErrorMessage(responsePayload, "list");
        toast.error(errorMessage);
        formikHelpers.setSubmitting(false);
      }
    } catch (error: any) {
      console.error("Filter XAR reports failed:", error);
      const errorMessage = getXarReportErrorMessage(error, "list");
      toast.error(errorMessage);
      formikHelpers.setSubmitting(false);
    }
  };

  const handleListingDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setListingDateRange(formattedValue);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleGenerateModalDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setGenerateDateRange(formattedValue);
  };

  const handleDateRangeSubmit = async () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    const updatedFilterValues = { ...filterValues };

    const payload: IApiPayload = {
      ...(apiPayload ?? {}),
      ...updatedFilterValues,
      startDate,
      endDate,
      objectIds: filterValues.assignedToAllObjects
        ? []
        : (filterValues.objectIds ?? []),
      reportType: 8
    };

    setApiPayload(payload);

    const xarPayload: any = {
      accountId: drawerSelectedAccountId,
      reportType: 8,
      startDate,
      endDate
    };

    if (payload?.driverId) {
      xarPayload.driverId = payload.driverId;
    }

    if (payload?.objectIds) {
      xarPayload.objectIds = payload.objectIds;
    }

    dispatch(setReportsFilterData(payload));

    try {
      const { type, payload } = await dispatch(listXarReportThunk(xarPayload));

      if (type === "reports/listXarReportThunk/fulfilled") {
        return;
      }

      const errorMessage = getXarReportErrorMessage(payload, "list");
      toast.error(errorMessage);
      setFilterOpen(true);
    } catch (error: any) {
      console.error("Load XAR report failed:", error);
      const errorMessage = getXarReportErrorMessage(error, "list");
      toast.error(errorMessage);
    }
  };

  const handleGenerateReport = () => {
    setOpenGenerateForm(true);
  };

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    setIsRefreshing(true);

    try {
      await handleDateRangeSubmit();
    } finally {
      refreshTimeoutRef.current = setTimeout(() => {
        setIsRefreshing(false);
      }, 1500);
    }
  };

  const handleSubmitGenerateReport = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    // Use the generateDateRange, not the listing dateValues
    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(generateDateRange);

    const xarPayload: any = {
      accountId: drawerSelectedAccountId,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      reportType: 8
    };

    if (values.reportMode === "byDriver") {
      if (values.driverId && values.driverId !== "") {
        xarPayload.driverId = values.driverId;
      } else if (values.keyId && values.keyId !== "") {
        xarPayload.keyId = values.keyId;
      }
    } else if (values.reportMode === "byObject") {
      if (values.assignedToAllObjects) {
        xarPayload.objectIds = [];
      } else {
        xarPayload.objectIds = values.objectIds || [];
      }
    }

    try {
      const { type, payload } = await dispatch(
        generateXarReportThunk(xarPayload)
      );

      if (type === "reports/generateXarReportThunk/fulfilled") {
        toast.success(t("reports.generateSuccess"));
        setOpenGenerateForm(false);
        handleDateRangeSubmit();
        formikHelpers.setSubmitting(false);
      } else {
        const errorMessage = getXarReportErrorMessage(
          payload,
          "generate",
          values.reportMode
        );
        toast.error(errorMessage);
        formikHelpers.setSubmitting(false);
      }
    } catch (error: any) {
      console.error("Generate XAR report failed:", error);
      const errorMessage = getXarReportErrorMessage(
        error,
        "generate",
        values.reportMode
      );
      toast.error(errorMessage);
      formikHelpers.setSubmitting(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 6);
    startDate.setHours(0, 0, 0, 0);

    const initialRange = {
      startDate: startDate,
      endDate: endDate
    };

    setListingDateRange(initialRange);
    setGenerateDateRange(initialRange);
    setDateValues(initialRange);
    dispatch(
      setReportsDateValues({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      })
    );
  }, []);

  useEffect(() => {
    if (
      drawerSelectedAccountId &&
      dateValues?.startDate &&
      dateValues?.endDate
    ) {
      const requestKey = `${drawerSelectedAccountId}-${dateValues.startDate}-${dateValues.endDate}`;

      if (lastRequestRef.current === requestKey) {
        return;
      }

      lastRequestRef.current = requestKey;

      handleDateRangeSubmit();
    }
  }, [drawerSelectedAccountId]);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
      dispatch(getUsersBasicInfoThunk(drawerSelectedAccountId || ""));
    }
  }, [drawerSelectedAccountId]);

  useEffect(() => {
    if (currentRoleName && !ADMIN_ROLES.includes(currentRoleName)) {
      navigate("/dashboard/reports");
      toast.error("You don't have permission to access XAR Reports");
    }
  }, [currentRoleName, navigate]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  if (currentRoleName && !ADMIN_ROLES.includes(currentRoleName)) {
    return null;
  }

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.xarReport")}
        disabled={reportsLoading}
      />
      <Box mx={4}>
        <Box position="relative">
          <ReportsFilterBar
            setFilterOpen={setFilterOpen}
            dateFormat={dateFormat}
            setDateFormat={setDateFormat}
            handleDateChange={handleListingDateChange}
            dateValues={listingDateRange}
            handleDateRangeSubmit={handleDateRangeSubmit}
            dateRangeLoading={xarReportLoading}
            disableExportButton={!formattedData?.length}
            reportType={8}
            reportExportTypeValue={2}
            openModal={openModal}
            setOpenModal={setOpenModal}
            exportModalButton={false}
            hideExportButton={true}
            hideScheduleButton={true}
            hideLoadReportButton={true}
            hideDateRangePicker={true}
            hideDateFormats={true}
            handleLoadReport={handleDateRangeSubmit}
            handleGenerateReport={handleGenerateReport}
          />
          <Box
            position="absolute"
            left="90px"
            top="50%"
            sx={{ transform: "translateY(-50%)" }}
          >
            <Tooltip title={t("buttonsText.refresh") || "Refresh"}>
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={isRefreshing || xarReportLoading}
                  size="small"
                  sx={{
                    color:
                      isRefreshing || xarReportLoading
                        ? theme.palette.grey[400]
                        : theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover
                    },
                    "& .MuiSvgIcon-root": {
                      animation:
                        isRefreshing || xarReportLoading
                          ? "spin 1s linear infinite"
                          : "none"
                    },
                    "@keyframes spin": {
                      "0%": {
                        transform: "rotate(0deg)"
                      },
                      "100%": {
                        transform: "rotate(360deg)"
                      }
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <ReportsCommonFilterForm
          fetchOnDebounce={handleSkrrsObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={co2ReportFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={skrrsObjects}
          keys={accountKeys}
          dataLoading={skrrsLoading || objectTypesLoading || accountKeysLoading}
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          isCO2InnerForm
          formInitialValues={getHydratedFormInitialValues(
            xarReportFilterInitialValues
          )}
          formValidations={xarReportFilterValidationSchema}
          reportType={8}
          users={usersBasicInfo}
          showDateRangePicker={true}
          dateValues={listingDateRange}
          handleDateChange={handleListingDateChange}
          dateRangeLoading={xarReportLoading}
          driverIsRequired={false}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(xarReportFilterInitialValues)
            );
          }}
        />
        <ReactTable
          columns={tableColumns}
          data={xarReport || []}
          isSelectable={false}
          isLoading={xarReportLoading as any}
          paginationTotalRows={xarReport?.length || 0}
          defaultPageSize={100}
          paginationServer={false}
          hideTotalRows={true}
          sx={undefined}
          moduleName="xarReports"
          tableMainBoxStyles={{
            height: "calc(100vh - 314px)",
            border: "1px solid #D5D9E8",
            borderRadius: "10px",
            marginBottom: "10vh",
            padding: "1px",
            "& > div > div > div:last-child": {
              display: "none !important"
            }
          }}
          mainTableStyles={{}}
        />
        <GenerateXarReportForm
          open={openGenerateForm}
          onClose={() => setOpenGenerateForm(false)}
          onSubmit={handleSubmitGenerateReport}
          theme={theme}
          dataLoading={xarReportLoading}
          objects={Array.isArray(skrrsObjects) ? skrrsObjects : []}
          formInitialValues={xarReportFormInitialValues}
          formValidations={xarReportFormValidationSchema(t)}
          fetchOnDebounce={handleSkrrsObjectsSearch}
          fetchAccountOnDebounce={handleAccountSearch}
          handleDateChange={handleGenerateModalDateChange}
          dateValues={generateDateRange}
          dateRangeLoading={xarReportLoading}
          searchedMinimalAccounts={searchedMinimalAccounts}
          searchedMinimalAccountsLoading={searchedMinimalAccountsLoading}
          users={usersBasicInfo}
          accountKeys={accountKeys}
          accountKeysLoading={accountKeysLoading}
        />
      </Box>
    </Box>
  );
};

export default XarReports;
